import { useState } from "react";
import { PageHeader } from "../components/layout/PageHeader";
import { Workflow, Plus, Mail, MessageSquare, Tag, Webhook, Bell, Zap, GitBranch, Clock, FileOutput, Sparkles } from "lucide-react";
import { cn } from "../lib/utils";

const TEMPLATES = [
  { id: "lead-magnet", name: "Lead Magnet Delivery", desc: "Trigger on form submit → email lead magnet → wait 1 day → reminder.", steps: 5 },
  { id: "webinar-reg", name: "Webinar Registration Follow-Up", desc: "Confirm → daily reminder → 1hr reminder → live.", steps: 7 },
  { id: "webinar-replay", name: "Webinar Replay Follow-Up", desc: "Send replay → highlight reel → cart close warning.", steps: 6 },
  { id: "book-launch", name: "Book Launch Campaign", desc: "Pre-launch teasers → launch day → reviews ask → cart close.", steps: 9 },
  { id: "cart-close", name: "Cart Close Sequence", desc: "Warning → last chance email + SMS → recap.", steps: 4 },
  { id: "client-onboard", name: "Client Onboarding", desc: "Welcome → workbook → kickoff call → check-in.", steps: 6 },
];

const BLOCKS = [
  { id: "trigger", label: "Trigger", icon: Zap, tone: "aqua" },
  { id: "delay", label: "Delay", icon: Clock, tone: "muted" },
  { id: "condition", label: "Condition", icon: GitBranch, tone: "lime" },
  { id: "email", label: "Email", icon: Mail, tone: "muted" },
  { id: "sms", label: "SMS", icon: MessageSquare, tone: "muted" },
  { id: "tag", label: "Tag", icon: Tag, tone: "muted" },
  { id: "webhook", label: "Webhook", icon: Webhook, tone: "muted" },
  { id: "notify", label: "Notify", icon: Bell, tone: "muted" },
  { id: "ai", label: "AI Generate", icon: Sparkles, tone: "aqua" },
  { id: "export", label: "Export", icon: FileOutput, tone: "muted" },
];

const SAMPLE_FLOW = [
  { id: 1, type: "trigger", label: "Form submitted: Workbook download" },
  { id: 2, type: "delay", label: "Wait 10 minutes" },
  { id: 3, type: "email", label: "Send: Lead magnet delivery" },
  { id: 4, type: "delay", label: "Wait 1 day" },
  { id: 5, type: "condition", label: "If link clicked" },
  { id: 6, type: "sms", label: "Send SMS reminder (if not clicked)" },
  { id: 7, type: "tag", label: "Add tag: nurture-stage-2" },
];

export default function AutomationLab() {
  const [selected, setSelected] = useState(SAMPLE_FLOW);

  return (
    <div data-testid="automation-lab-page" className="space-y-8">
      <PageHeader
        kicker="AutomationLab"
        title="Workflows: Trigger → Condition → Action"
        description="Visual workflow builder. Drop blocks, connect logic, ship lifecycles. Email, SMS, AI generations, exports, webhooks — all native."
        actions={
          <button
            data-testid="auto-new-btn"
            className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
          >
            <Plus className="h-4 w-4" /> New Workflow
          </button>
        }
      />

      {/* Templates */}
      <section className="space-y-3">
        <Eyebrow>Workflow Templates</Eyebrow>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TEMPLATES.map((t, i) => (
            <div
              key={t.id}
              data-testid={`auto-tpl-${t.id}`}
              className="rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 pl-lift cursor-pointer"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="flex items-center gap-2">
                <Workflow className="h-4 w-4 text-[var(--pl-aqua)]" />
                <div className="font-medium text-[var(--pl-text)]">{t.name}</div>
              </div>
              <div className="text-xs text-[var(--pl-text-muted)] mt-2">{t.desc}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-aqua)] mt-3">
                {t.steps} steps
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Builder */}
      <section className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Block palette */}
        <aside data-testid="block-palette" className="rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 space-y-2">
          <Eyebrow>Blocks</Eyebrow>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {BLOCKS.map((b) => {
              const I = b.icon;
              return (
                <div
                  key={b.id}
                  data-testid={`block-${b.id}`}
                  className={cn(
                    "rounded-xl p-3 border bg-white/[0.03] flex flex-col items-center gap-1 text-[11px] cursor-grab",
                    b.tone === "aqua"
                      ? "border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                      : b.tone === "lime"
                      ? "border-[var(--pl-lime)]/40 text-[var(--pl-lime)]"
                      : "border-[var(--pl-border)] text-[var(--pl-text-muted)]"
                  )}
                >
                  <I className="h-4 w-4" />
                  {b.label}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Canvas */}
        <div className="lg:col-span-3 rounded-2xl p-5 border border-[var(--pl-border)] bg-[var(--pl-surface)]/40 backdrop-blur-md min-h-[480px] relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(39,230,209,0.10),transparent_45%)] pointer-events-none" />
          <Eyebrow>Lead Magnet Delivery · Active</Eyebrow>
          <div className="mt-4 space-y-3 relative">
            {selected.map((step, i) => {
              const block = BLOCKS.find((b) => b.id === step.type) || BLOCKS[0];
              const I = block.icon;
              return (
                <div key={step.id} className="flex items-start gap-3" data-testid={`flow-step-${step.id}`}>
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        "h-10 w-10 rounded-xl grid place-items-center border",
                        block.tone === "aqua"
                          ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                          : block.tone === "lime"
                          ? "bg-[var(--pl-lime)]/15 border-[var(--pl-lime)]/40 text-[var(--pl-lime)]"
                          : "bg-white/[0.04] border-[var(--pl-border)] text-[var(--pl-text)]"
                      )}
                    >
                      <I className="h-4 w-4" />
                    </div>
                    {i < selected.length - 1 && (
                      <div className="h-6 w-px bg-[var(--pl-border)] my-1" />
                    )}
                  </div>
                  <div className="flex-1 rounded-xl border border-[var(--pl-border)] bg-white/[0.03] px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
                      {block.label}
                    </div>
                    <div className="text-sm text-[var(--pl-text)] mt-0.5">{step.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function Eyebrow({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">{children}</div>;
}
